import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, ExternalLink, Trash2, Upload, Image as ImageIcon, Search, Building2, Zap, Check, X, Sparkles, Globe, AlertTriangle, Layers, History } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const inputCls = "bg-[#0A1F1A] border border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-0 rounded-lg text-sm";

const driveThumb = (fileId) => fileId ? `${process.env.REACT_APP_BACKEND_URL}/api/inmuebles-drive/thumb/${fileId}` : null;

const statusOptions = {
  link_wasi: ['PENDIENTE', 'SUBIDO', 'NO TIENE'],
  link_web: ['PENDIENTE', 'SUBIDO', 'NO TIENE'],
  documentos: ['PENDIENTE', 'COMPLETOS', 'NO TIENE'],
};

const STATUS_COLOR = {
  PENDIENTE: 'bg-orange-500/10 text-orange-300 border-orange-500/20',
  SUBIDO: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  COMPLETOS: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'NO TIENE': 'bg-red-500/10 text-red-300 border-red-500/20',
};

export default function InmueblesDrive() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [mergeModal, setMergeModal] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [matchingWasi, setMatchingWasi] = useState(false);
  const [matchProgress, setMatchProgress] = useState(null);
  const [syncingWeb, setSyncingWeb] = useState(false);
  const [webProgress, setWebProgress] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditProgress, setAuditProgress] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [discrepancyModal, setDiscrepancyModal] = useState(null);
  const [conflictModal, setConflictModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all'); // all | wasi | web
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const debounceRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, b] = await Promise.all([
        api.get('/inmuebles-drive'),
        api.get('/inmuebles-drive/brokers'),
      ]);
      setItems(data);
      setBrokers(b);
    } catch { toast.error('Error cargando inmuebles'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/inmuebles-drive/sync');
      toast.success(`Sincronizacion completa: ${r.created} nuevos, ${r.skipped} existentes`);
      if (r.errors && r.errors.length) toast.warning(r.errors.slice(0, 3).join(' | '));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error en sincronizacion'); }
    setSyncing(false);
  };

  const pollWasiMatchStatus = async () => {
    let stopped = false;
    // Return a promise that resolves when the job finishes
    return new Promise((resolve) => {
      const tick = async () => {
        if (stopped) return;
        try {
          const s = await api.get('/inmuebles-drive/auto-match-wasi/status');
          setMatchProgress(s);
          if (s.status === 'done' || s.status === 'error' || s.status === 'idle') {
            stopped = true;
            resolve(s);
            return;
          }
        } catch { /* ignore transient */ }
        setTimeout(tick, 3500);
      };
      tick();
    });
  };

  const autoMatchWasi = async () => {
    setMatchingWasi(true);
    setMatchProgress({ status: 'running', phase: 'starting', total_wasi: 0, total_drive: 0, wasi_hashed: 0, drive_hashed: 0, auto_linked: 0, suggestions: 0 });
    try {
      const start = await api.post('/inmuebles-drive/auto-match-wasi');
      if (start.existing) toast.info('Ya hay un análisis en curso — te muestro el progreso.');
      else toast.info('Análisis iniciado. Puedes cerrar esta ventana; corre en segundo plano.');
      const final = await pollWasiMatchStatus();
      if (final.status === 'done') {
        const r = final.result || {};
        toast.success(`✅ ${r.auto_linked || 0} auto-vinculado(s), 🎯 ${r.suggestions || 0} sugerencia(s) sobre ${r.scanned || 0} inmueble(s)`);
        load();
      } else if (final.status === 'error') {
        toast.error(final.error || 'Error en el análisis');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error iniciando análisis');
    }
    setMatchingWasi(false);
    setTimeout(() => setMatchProgress(null), 4000);
  };

  // On mount, resume tracking if a job is already running (survives page reload)
  useEffect(() => {
    (async () => {
      try {
        const s = await api.get('/inmuebles-drive/auto-match-wasi/status');
        if (s && s.status === 'running') {
          setMatchingWasi(true);
          setMatchProgress(s);
          const final = await pollWasiMatchStatus();
          if (final.status === 'done') { toast.success('Análisis WASI completado'); load(); }
          setMatchingWasi(false);
          setTimeout(() => setMatchProgress(null), 4000);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const acceptWasiSuggestion = async (id) => {
    try {
      const r = await api.post(`/inmuebles-drive/${id}/accept-wasi-suggestion`);
      toast.success('Sugerencia aceptada — link WASI asignado');
      setItems(curr => curr.map(x => x.id === id ? { ...x, link_wasi: 'SUBIDO', link_wasi_url: r.wasi_url, wasi_match_suggestion: null } : x));
    } catch (e) { console.error('acceptWasiSuggestion error', e); toast.error(e?.response?.data?.detail || 'Error al aceptar sugerencia'); }
  };

  const dismissWasiSuggestion = async (id) => {
    try {
      await api.post(`/inmuebles-drive/${id}/dismiss-wasi-suggestion`);
      setItems(curr => curr.map(x => x.id === id ? { ...x, wasi_match_suggestion: null } : x));
      toast.success('Sugerencia descartada');
    } catch (e) { console.error('dismissWasiSuggestion error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const resolveWasiConflict = async (rowId, idProperty) => {
    try {
      const r = await api.post(`/inmuebles-drive/${rowId}/resolve-wasi-conflict`, { id_property: idProperty });
      toast.success('Conflicto resuelto — WASI vinculado');
      setItems(curr => curr.map(x => x.id === rowId ? { ...x, link_wasi: 'SUBIDO', link_wasi_url: r.wasi_url, wasi_match_conflict: null } : x));
      setConflictModal(null);
      load(); // Reload to see the effect on other conflicting rows
    } catch (e) { console.error('resolveWasiConflict error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const dismissWasiConflict = async (rowId) => {
    try {
      await api.post(`/inmuebles-drive/${rowId}/dismiss-wasi-conflict`);
      setItems(curr => curr.map(x => x.id === rowId ? { ...x, wasi_match_conflict: null } : x));
      setConflictModal(null);
      toast.success('Conflicto descartado');
    } catch (e) { console.error('dismissWasiConflict error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const deleteInmuebleRow = async (rowId) => {
    if (!confirm('¿Eliminar esta fila? No borra fotos del Drive ni la ficha de WASI, solo la fila del CRM.')) return;
    try {
      await api.del(`/inmuebles-drive/${rowId}`);
      setItems(curr => curr.filter(x => x.id !== rowId));
      setConflictModal(null);
      toast.success('Fila eliminada');
    } catch (e) { console.error('deleteInmuebleRow error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const mergeRows = async (keeperId, otherId) => {
    if (!confirm('¿Fusionar? El "otro" inmueble será eliminado y sus datos completarán los espacios vacíos del inmueble mantenido. Esta acción no se puede deshacer.')) return;
    try {
      await api.post(`/inmuebles-drive/${keeperId}/merge`, { other_id: otherId });
      toast.success('Inmuebles fusionados');
      setMergeModal(null);
      load();
    } catch (e) { console.error('mergeRows error', e); toast.error(e?.response?.data?.detail || 'Error al fusionar'); }
  };

  const assignBroker = async (rowId, brokerUserId) => {
    try {
      await api.put(`/inmuebles-drive/${rowId}`, { broker_user_id: brokerUserId });
      const chosen = brokers.find(b => b.id === brokerUserId);
      setItems(curr => curr.map(x => x.id === rowId ? { ...x, broker_user_id: brokerUserId, broker_user_name: chosen?.name || x.broker_user_name } : x));
      toast.success('Broker asignado');
    } catch (e) { console.error('assignBroker error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const pollWebSyncStatus = () => new Promise((resolve) => {
    const tick = async () => {
      try {
        const s = await api.get('/inmuebles-drive/sync-website/status');
        setWebProgress(s);
        if (s.status === 'done' || s.status === 'error' || s.status === 'idle') return resolve(s);
      } catch { /* ignore */ }
      setTimeout(tick, 3500);
    };
    tick();
  });

  const syncWebsite = async () => {
    setSyncingWeb(true);
    setWebProgress({ status: 'running', phase: 'starting', total_web: 0, total_drive: 0, web_hashed: 0, drive_hashed: 0, auto_linked: 0, suggestions: 0 });
    try {
      const start = await api.post('/inmuebles-drive/sync-website');
      if (start.existing) toast.info('Ya hay un análisis en curso — te muestro el progreso.');
      else toast.info('Analizando propiedades de somosselecto.com... corre en segundo plano.');
      const final = await pollWebSyncStatus();
      if (final.status === 'done') {
        const r = final.result || {};
        toast.success(`✅ ${r.auto_linked || 0} auto-vinculados · 🎯 ${r.suggestions || 0} sugerencias · ⚠️ ${r.not_in_web || 0} en WASI pero no en la web`);
        load();
      } else if (final.status === 'error') { toast.error(final.error || 'Error'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error iniciando análisis'); }
    setSyncingWeb(false);
    setTimeout(() => setWebProgress(null), 4000);
  };

  const acceptWebsiteSuggestion = async (id) => {
    try {
      const r = await api.post(`/inmuebles-drive/${id}/accept-website-suggestion`);
      toast.success('Vinculado a la web');
      setItems(curr => curr.map(x => x.id === id ? { ...x, link_web_url: r.url, link_web_auto_matched: true, web_match_suggestion: null } : x));
    } catch (e) { console.error('acceptWebsiteSuggestion error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const dismissWebsiteSuggestion = async (id) => {
    try {
      await api.post(`/inmuebles-drive/${id}/dismiss-website-suggestion`);
      setItems(curr => curr.map(x => x.id === id ? { ...x, web_match_suggestion: null } : x));
    } catch (e) { console.error('dismissWebsiteSuggestion error', e); toast.error(e?.response?.data?.detail || 'Error'); }
  };

  // ---- Data-consistency audit (WASI vs Web) ----
  const pollAuditStatus = () => new Promise((resolve) => {
    const tick = async () => {
      try {
        const s = await api.get('/inmuebles-drive/audit-data/status');
        setAuditProgress(s);
        if (s.status === 'done' || s.status === 'error' || s.status === 'idle') return resolve(s);
      } catch { /* ignore */ }
      setTimeout(tick, 3000);
    };
    tick();
  });

  const auditData = async () => {
    setAuditing(true);
    setAuditProgress({ status: 'running', phase: 'starting', total: 0, checked: 0, with_diffs: 0, total_diffs: 0 });
    try {
      const start = await api.post('/inmuebles-drive/audit-data');
      if (start.existing) toast.info('Ya hay una auditoría en curso — te muestro el progreso.');
      else toast.info('Auditando datos de WASI vs somosselecto.com…');
      const final = await pollAuditStatus();
      if (final.status === 'done') {
        const r = final.result || {};
        toast.success(`Auditoría lista: ${r.with_diffs || 0} inmueble(s) con inconsistencias (${r.total_diffs || 0} campos)`);
        load();
      } else if (final.status === 'error') { toast.error(final.error || 'Error'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error iniciando auditoría'); }
    setAuditing(false);
    setTimeout(() => setAuditProgress(null), 4000);
  };

  const dismissDiscrepancies = async (id) => {
    try {
      await api.post(`/inmuebles-drive/${id}/dismiss-discrepancies`);
      setItems(curr => curr.map(x => x.id === id ? { ...x, data_discrepancies: [] } : x));
      setDiscrepancyModal(null);
      toast.success('Discrepancias marcadas como revisadas');
    } catch { toast.error('Error'); }
  };

  // ---- Change log (WASI + Web) ----
  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const events = await api.get('/inmuebles/change-log?limit=500');
      setHistoryEvents(events || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error cargando historial'); }
    setHistoryLoading(false);
  };

  // ---- One-click "Sincronizar Todo": Drive → Web → WASI photos → Audit ----
  const syncAll = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    let summary = { drive: null, wasiSync: null, web: null, wasi: null, audit: null };
    try {
      // 1. Drive sync (fast, blocking)
      setSyncing(true);
      try {
        const r = await api.post('/inmuebles-drive/sync');
        summary.drive = r;
        toast.info(`Drive: ${r.created} nuevos, ${r.skipped} existentes`);
      } catch (e) { toast.error(`Drive: ${e.response?.data?.detail || 'error'}`); }
      setSyncing(false);

      // 2. WASI sync — populates wasi_property_snapshots from the WASI API (this is
      // the essential first step so the auto-match / WASI-only import have data).
      try {
        toast.info('Consultando WASI...');
        const r = await api.post('/wasi/sync');
        summary.wasiSync = r;
        toast.info(`WASI: ${r.properties_scanned || 0} propiedades leídas`);
      } catch (e) { toast.error(`WASI sync: ${e.response?.data?.detail || 'error'}`); }

      // 3. Web sync (background job)
      setSyncingWeb(true);
      setWebProgress({ status: 'running', phase: 'starting', total_web: 0, total_drive: 0, web_hashed: 0, drive_hashed: 0, auto_linked: 0, suggestions: 0 });
      try {
        await api.post('/inmuebles-drive/sync-website');
        const final = await pollWebSyncStatus();
        summary.web = final.result || {};
        if (final.status === 'error') toast.error(`Web: ${final.error || 'error'}`);
      } catch (e) { toast.error(`Web: ${e.response?.data?.detail || 'error'}`); }
      setSyncingWeb(false);
      setTimeout(() => setWebProgress(null), 2000);

      // 4. WASI auto-match (background job)
      setMatchingWasi(true);
      setMatchProgress({ status: 'running', phase: 'starting', total_wasi: 0, total_drive: 0, wasi_hashed: 0, drive_hashed: 0, auto_linked: 0, suggestions: 0 });
      try {
        await api.post('/inmuebles-drive/auto-match-wasi');
        const final = await pollWasiMatchStatus();
        summary.wasi = final.result || {};
        if (final.status === 'error') toast.error(`WASI: ${final.error || 'error'}`);
      } catch (e) { toast.error(`WASI: ${e.response?.data?.detail || 'error'}`); }
      setMatchingWasi(false);
      setTimeout(() => setMatchProgress(null), 2000);

      // 5. Data audit (background job)
      setAuditing(true);
      setAuditProgress({ status: 'running', phase: 'starting', total: 0, checked: 0, with_diffs: 0, total_diffs: 0 });
      try {
        await api.post('/inmuebles-drive/audit-data');
        const final = await pollAuditStatus();
        summary.audit = final.result || {};
        if (final.status === 'error') toast.error(`Auditoría: ${final.error || 'error'}`);
      } catch (e) { toast.error(`Auditoría: ${e.response?.data?.detail || 'error'}`); }
      setAuditing(false);
      setTimeout(() => setAuditProgress(null), 2000);

      // Consolidated summary
      const parts = [];
      if (summary.drive) parts.push(`📁 ${summary.drive.created || 0} nuevos`);
      if (summary.wasiSync) parts.push(`🏢 ${summary.wasiSync.properties_scanned || 0} WASI leídas`);
      if (summary.web) parts.push(`🌐 ${summary.web.auto_linked || 0} vinc. web`);
      if (summary.wasi) parts.push(`✨ ${summary.wasi.wasi_only_imported || 0} nuevas de WASI · ${summary.wasi.auto_linked || 0} auto-vinc.`);
      if (summary.audit) parts.push(`⚠️ ${summary.audit.with_diffs || 0} con inconsistencias`);
      toast.success(`Sincronización completa · ${parts.join(' · ')}`, { duration: 8000 });
      load();
    } finally {
      setSyncingAll(false);
    }
  };

  const updateField = async (id, field, value) => {
    const prev = items;
    setItems(items.map(x => x.id === id ? { ...x, [field]: value } : x));
    try { await api.put(`/inmuebles-drive/${id}`, { [field]: value }); }
    catch { setItems(prev); toast.error('Error al actualizar'); }
  };

  // Debounced update for text fields (inmueble_nombre, notas)
  const updateFieldDebounced = (id, field, value) => {
    setItems(curr => curr.map(x => x.id === id ? { ...x, [field]: value } : x));
    const key = `${id}-${field}`;
    if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(async () => {
      try { await api.put(`/inmuebles-drive/${id}`, { [field]: value }); }
      catch { toast.error('Error al guardar'); }
    }, 600);
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Eliminar este inmueble del modulo? (No se borra de Drive)')) return;
    try { await api.delete(`/inmuebles-drive/${id}`); load(); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };

  const triggerUpload = (id) => {
    setUploadingFor(id);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadingFor) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagen demasiado grande (max 3MB)'); setUploadingFor(null); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      await updateField(uploadingFor, 'cover_image', reader.result);
      setUploadingFor(null);
      toast.success('Portada actualizada');
    };
    reader.readAsDataURL(file);
  };

  const filtered = items.filter(it => {
    if (brokerFilter !== 'all' && it.broker_user_id !== brokerFilter) return false;
    if (statusFilter === 'disponible' && !it.disponible) return false;
    if (statusFilter === 'no_disponible' && it.disponible) return false;
    if (sourceFilter === 'drive' && it.source === 'wasi') return false;
    if (sourceFilter === 'wasi' && it.source !== 'wasi') return false;
    if (sourceFilter === 'drive_no_wasi' && (it.source === 'wasi' || it.link_wasi_url)) return false;
    if (sourceFilter === 'no_web' && (it.link_web_url)) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(it.session_name?.toLowerCase().includes(s) || it.inmueble_nombre?.toLowerCase().includes(s) || it.broker_user_name?.toLowerCase().includes(s))) return false;
    }
    return true;
  }).sort((a, b) => {
    // Rows with data inconsistencies float to the top
    const aDiffs = (a.data_discrepancies || []).length;
    const bDiffs = (b.data_discrepancies || []).length;
    if (aDiffs !== bDiffs) return bDiffs - aDiffs;
    // Then WASI conflicts
    if (!!a.wasi_match_conflict !== !!b.wasi_match_conflict) return b.wasi_match_conflict ? 1 : -1;
    return 0;
  });

  const StatusBadge = ({ id, field, value }) => {
    const opts = statusOptions[field];
    return (
      <Select value={value || 'PENDIENTE'} onValueChange={v => updateField(id, field, v)}>
        <SelectTrigger className={`h-7 text-[10px] px-2 rounded-full border w-auto min-w-[100px] ${STATUS_COLOR[value] || STATUS_COLOR.PENDIENTE}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0A1F1A] border-white/10">
          {opts.map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="space-y-6" data-testid="inmuebles-drive-page">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#A38560]/10 border border-[#A38560]/20 flex items-center justify-center">
              <Building2 size={20} className="text-[#A38560]" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-outfit text-3xl font-medium tracking-tight text-white">Inmuebles</h1>
              <p className="text-xs text-white/40 mt-0.5 tracking-wide">Listings activos sincronizados con Drive</p>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2 shrink-0 w-fit flex-wrap">
            <button onClick={openHistory} data-testid="open-history-btn"
              title="Ver todos los cambios detectados en WASI y en la web"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 text-sm font-medium rounded-xl transition-all">
              <History size={15} />
              Historial de cambios
            </button>
            <button onClick={syncAll} disabled={syncingAll} data-testid="sync-all-btn"
              title="Ejecuta Drive → Web → WASI → Auditoría en un solo click"
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#B39670] hover:to-[#9B7F57] text-[#03110D] text-sm font-semibold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-[#A38560]/20">
              <Layers size={15} className={syncingAll ? 'animate-pulse' : ''} />
              {syncingAll ? 'Sincronizando todo...' : 'Sincronizar Todo'}
            </button>
            <button onClick={auditData} disabled={auditing || syncingAll} data-testid="audit-data-btn"
              title="Compara datos entre WASI y somosselecto.com y detecta inconsistencias"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-amber-400 border border-amber-500/25 text-sm font-medium rounded-xl transition-all disabled:opacity-50">
              <AlertTriangle size={15} className={auditing ? 'animate-pulse' : ''} />
              {auditing ? 'Auditando...' : 'Auditar Datos'}
            </button>
            <button onClick={syncWebsite} disabled={syncingWeb || syncing || matchingWasi || syncingAll} data-testid="sync-website-btn"
              title="Analiza somosselecto.com y vincula inmuebles del Drive"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-blue-400 border border-blue-500/25 text-sm font-medium rounded-xl transition-all disabled:opacity-50">
              <Globe size={15} className={syncingWeb ? 'animate-pulse' : ''} />
              {syncingWeb ? 'Analizando web...' : 'Sync Web'}
            </button>
            <button onClick={autoMatchWasi} disabled={matchingWasi || syncing || syncingWeb || syncingAll} data-testid="auto-match-wasi-btn"
              title="Analiza fotos y auto-vincula inmuebles a sus fichas WASI"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-[#A38560] border border-[#A38560]/25 text-sm font-medium rounded-xl transition-all disabled:opacity-50">
              <Sparkles size={15} className={matchingWasi ? 'animate-pulse' : ''} />
              {matchingWasi ? 'Analizando fotos...' : 'Auto-vincular WASI'}
            </button>
            <button onClick={sync} disabled={syncing || matchingWasi || syncingWeb || syncingAll} data-testid="sync-drive-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 text-sm font-medium rounded-xl transition-all disabled:opacity-50">
              <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Sincronizando...' : 'Sincronizar Drive'}
            </button>
          </div>
        )}
        {!isAdmin && (
          <div className="flex gap-2 shrink-0 w-fit flex-wrap">
            <button onClick={openHistory} data-testid="open-history-btn-broker"
              title="Ver los cambios detectados en tus inmuebles (WASI y web)"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 text-sm font-medium rounded-xl transition-all">
              <History size={15} />
              Historial de cambios
            </button>
          </div>
        )}
      </div>

      {/* Website sync progress banner */}
      {webProgress && (
        <div data-testid="website-sync-progress" className="rounded-2xl border border-blue-500/30 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent p-4">
          <div className="flex items-center gap-3 mb-3">
            <Globe size={18} className={`text-blue-400 ${webProgress.status === 'running' ? 'animate-pulse' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className="font-outfit text-sm font-semibold text-white">
                {webProgress.status === 'done' ? '✅ Análisis de somosselecto.com completado' :
                 webProgress.status === 'error' ? '❌ Error en el análisis' :
                 webProgress.phase === 'web_hashing' ? 'Analizando propiedades del sitio web…' :
                 webProgress.phase === 'matching' ? 'Comparando con inmuebles del Drive…' :
                 'Iniciando análisis…'}
              </p>
              <p className="text-[11px] text-white/50 mt-0.5">
                {webProgress.status === 'done'
                  ? `${webProgress.result?.auto_linked || 0} auto-vinculados · ${webProgress.result?.suggestions || 0} sugerencias · ${webProgress.result?.not_in_web || 0} en WASI pero no en la web`
                  : webProgress.phase === 'web_hashing'
                    ? `Web: ${webProgress.web_hashed || 0}/${webProgress.total_web || '?'} propiedades hasheadas`
                    : `Drive: ${webProgress.drive_hashed || 0}/${webProgress.total_drive || '?'} · ✅ ${webProgress.auto_linked || 0} · 🎯 ${webProgress.suggestions || 0}`}
              </p>
            </div>
          </div>
          {webProgress.status === 'running' && (
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500"
                style={{ width: webProgress.phase === 'web_hashing'
                  ? `${webProgress.total_web ? (webProgress.web_hashed / webProgress.total_web) * 50 : 0}%`
                  : `${50 + (webProgress.total_drive ? (webProgress.drive_hashed / webProgress.total_drive) * 50 : 0)}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Auto-match WASI progress banner */}
      {matchProgress && (
        <div data-testid="wasi-match-progress" className="rounded-2xl border border-[#A38560]/30 bg-gradient-to-r from-[#A38560]/10 via-[#A38560]/5 to-transparent p-4">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles size={18} className={`text-[#A38560] ${matchProgress.status === 'running' ? 'animate-pulse' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className="font-outfit text-sm font-semibold text-white">
                {matchProgress.status === 'done' ? '✅ Análisis WASI completado' :
                 matchProgress.status === 'error' ? '❌ Error en el análisis' :
                 matchProgress.phase === 'wasi_hashing' ? 'Analizando fotos de WASI…' :
                 matchProgress.phase === 'matching' ? 'Comparando con inmuebles del Drive…' :
                 'Iniciando análisis…'}
              </p>
              <p className="text-[11px] text-white/50 mt-0.5">
                {matchProgress.status === 'done'
                  ? `${matchProgress.result?.auto_linked || 0} auto-vinculados · ${matchProgress.result?.suggestions || 0} sugerencias · ${matchProgress.result?.scanned || 0} analizados`
                  : matchProgress.phase === 'wasi_hashing'
                    ? `WASI: ${matchProgress.wasi_hashed || 0}/${matchProgress.total_wasi || '?'} propiedades hasheadas`
                    : `Drive: ${matchProgress.drive_hashed || 0}/${matchProgress.total_drive || '?'} · ✅ ${matchProgress.auto_linked || 0} · 🎯 ${matchProgress.suggestions || 0}`
                }
              </p>
            </div>
          </div>
          {matchProgress.status === 'running' && (
            <div className="space-y-1.5">
              {/* WASI progress bar */}
              <div>
                <div className="flex justify-between text-[10px] text-white/40 mb-0.5">
                  <span>Fase 1 · Análisis WASI</span>
                  <span>{matchProgress.total_wasi ? Math.round((matchProgress.wasi_hashed / matchProgress.total_wasi) * 100) : 0}%</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#A38560] to-[#8B6F47] transition-all duration-500" style={{ width: `${matchProgress.total_wasi ? (matchProgress.wasi_hashed / matchProgress.total_wasi) * 100 : 0}%` }} />
                </div>
              </div>
              {/* Drive progress bar */}
              <div>
                <div className="flex justify-between text-[10px] text-white/40 mb-0.5">
                  <span>Fase 2 · Emparejamiento Drive</span>
                  <span>{matchProgress.total_drive ? Math.round((matchProgress.drive_hashed / matchProgress.total_drive) * 100) : 0}%</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500" style={{ width: `${matchProgress.total_drive ? (matchProgress.drive_hashed / matchProgress.total_drive) * 100 : 0}%` }} />
                </div>
              </div>
              <p className="text-[10px] text-white/30 italic pt-1">Puedes navegar a otras páginas — el análisis continúa en segundo plano.</p>
            </div>
          )}
        </div>
      )}

      {/* Data-audit progress banner */}
      {auditProgress && (
        <div data-testid="audit-progress" className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle size={18} className={`text-amber-400 ${auditProgress.status === 'running' ? 'animate-pulse' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className="font-outfit text-sm font-semibold text-white">
                {auditProgress.status === 'done' ? '✅ Auditoría de datos completada' :
                 auditProgress.status === 'error' ? '❌ Error en la auditoría' :
                 'Comparando datos WASI vs somosselecto.com…'}
              </p>
              <p className="text-[11px] text-white/50 mt-0.5">
                {auditProgress.status === 'done'
                  ? `${auditProgress.result?.with_diffs || 0} inmueble(s) con inconsistencias · ${auditProgress.result?.total_diffs || 0} campos totales`
                  : `${auditProgress.checked || 0}/${auditProgress.total || '?'} inmuebles revisados · ⚠️ ${auditProgress.with_diffs || 0} con diferencias`}
              </p>
            </div>
          </div>
          {auditProgress.status === 'running' && (
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
                style={{ width: `${auditProgress.total ? (auditProgress.checked / auditProgress.total) * 100 : 0}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Header stats board */}
      {items.length > 0 && (() => {
        const total = items.length;
        const conWasi = items.filter(x => x.link_wasi_url).length;
        const conDrive = items.filter(x => x.source !== 'wasi').length;
        const sinWasi = items.filter(x => x.source !== 'wasi' && !x.link_wasi_url).length;
        const sinWeb = items.filter(x => !x.link_web_url).length;
        const withDiff = items.filter(x => (x.data_discrepancies || []).length > 0).length;
        const Card = ({ label, value, tone, filter, testid }) => (
          <button onClick={() => filter && setSourceFilter(filter)} data-testid={testid}
            className={`flex-1 min-w-[110px] text-left px-4 py-3 rounded-2xl border transition-all ${
              tone === 'gold' ? 'bg-[#A38560]/10 border-[#A38560]/25 hover:bg-[#A38560]/15' :
              tone === 'amber' ? 'bg-amber-500/10 border-amber-500/25 hover:bg-amber-500/15' :
              tone === 'blue' ? 'bg-blue-500/10 border-blue-500/25 hover:bg-blue-500/15' :
              tone === 'red' ? 'bg-red-500/10 border-red-500/25 hover:bg-red-500/15' :
              'bg-white/[0.03] border-white/5 hover:bg-white/[0.06]'
            }`}>
            <p className="text-[10px] uppercase tracking-widest text-white/50">{label}</p>
            <p className={`text-2xl font-outfit font-semibold mt-0.5 ${
              tone === 'gold' ? 'text-[#A38560]' :
              tone === 'amber' ? 'text-amber-300' :
              tone === 'blue' ? 'text-blue-300' :
              tone === 'red' ? 'text-red-300' : 'text-white'
            }`}>{value}</p>
          </button>
        );
        return (
          <div data-testid="stats-board" className="flex flex-wrap gap-3">
            <Card label="Total inmuebles" value={total} tone="default" filter="all" testid="stat-total" />
            <Card label="En WASI" value={conWasi} tone="gold" filter="wasi" testid="stat-wasi" />
            <Card label="En Drive" value={conDrive} tone="blue" filter="drive" testid="stat-drive" />
            <Card label="Sin WASI" value={sinWasi} tone="amber" filter="drive_no_wasi" testid="stat-no-wasi" />
            <Card label="Sin publicar en web" value={sinWeb} tone="blue" filter="no_web" testid="stat-no-web" />
            {withDiff > 0 && <Card label="Con inconsistencias" value={withDiff} tone="red" testid="stat-discrepancies" />}
          </div>
        );
      })()}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por sesion, nombre o broker..." className={`${inputCls} pl-9 h-10`} />
        </div>
        <Select value={brokerFilter} onValueChange={setBrokerFilter}>
          <SelectTrigger data-testid="broker-filter" className={`${inputCls} h-10`}><SelectValue placeholder="Todos los brokers" /></SelectTrigger>
          <SelectContent className="bg-[#0A1F1A] border-white/10">
            <SelectItem value="all">Todos los brokers</SelectItem>
            {brokers.filter(b => b.id).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.count})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`${inputCls} h-10`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A1F1A] border-white/10">
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="disponible">Disponibles</SelectItem>
            <SelectItem value="no_disponible">No disponibles</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger data-testid="source-filter" className={`${inputCls} h-10`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A1F1A] border-white/10">
            <SelectItem value="all">Todas las fuentes</SelectItem>
            <SelectItem value="drive">Solo Drive</SelectItem>
            <SelectItem value="wasi">Solo WASI</SelectItem>
            <SelectItem value="drive_no_wasi">Drive sin WASI</SelectItem>
            <SelectItem value="no_web">Sin publicar en web</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Counter */}
      <div className="flex items-center justify-between text-xs text-white/40">
        <span data-testid="results-count">{filtered.length} inmuebles {brokerFilter !== 'all' || search || statusFilter !== 'all' ? '(filtrados)' : ''}</span>
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#03110D] border-b border-white/5">
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider w-24">Portada</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Broker</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Inmueble</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider w-24">Disp.</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Sesion Fotos</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider w-28">Link Fotos</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Link Wasi</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Link Web</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Documentos</th>
                <th className="text-left px-3 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider min-w-[180px]">Notas</th>
                {isAdmin && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const canEdit = isAdmin || it.broker_user_id === user?.id;
                return (
                  <tr key={it.id} data-testid={`row-${it.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 align-top">
                      <button onClick={() => canEdit && triggerUpload(it.id)} disabled={!canEdit}
                        className="w-20 h-16 rounded-lg overflow-hidden bg-[#03110D] border border-white/10 flex items-center justify-center hover:border-[#A38560]/40 transition-colors group relative">
                        {(it.cover_image || it.cover_drive_id) ? (
                          <>
                            <img src={it.cover_image || driveThumb(it.cover_drive_id)} alt="portada"
                              loading="lazy"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            {canEdit && <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                              <Upload size={14} className="text-white" />
                            </div>}
                          </>
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-white/30 group-hover:text-[#A38560]">
                            <ImageIcon size={16} />
                            <span className="text-[9px] uppercase tracking-wider">Subir</span>
                          </div>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[140px]">
                      {isAdmin ? (
                        <Select value={it.broker_user_id || 'unassigned'} onValueChange={v => assignBroker(it.id, v === 'unassigned' ? '' : v)}>
                          <SelectTrigger data-testid={`broker-select-${it.id}`} className={`${inputCls} h-8 text-xs`}>
                            <SelectValue placeholder="Sin asignar">
                              <span className="text-xs text-white/80 font-medium truncate">{it.broker_user_name || it.broker_folder_name || 'Sin asignar'}</span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A1F1A] border-white/10">
                            <SelectItem value="unassigned">Sin asignar</SelectItem>
                            {brokers.filter(b => b.id).map(b => <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-white/80 font-medium">{it.broker_user_name || it.broker_folder_name}</span>
                      )}
                      {it.source === 'wasi' && (
                        <span data-testid={`source-wasi-${it.id}`} className="mt-1 inline-flex items-center gap-1 text-[9px] text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded-full px-1.5 py-0.5">
                          <Sparkles size={8} /> Solo WASI
                        </span>
                      )}
                      {it.source !== 'wasi' && !it.link_wasi_url && (
                        <span data-testid={`no-wasi-${it.id}`} className="mt-1 inline-flex items-center gap-1 text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/25 rounded-full px-1.5 py-0.5">
                          <AlertTriangle size={8} /> Sin WASI
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top min-w-[140px]">
                      <Input value={it.inmueble_nombre || ''} onChange={e => updateFieldDebounced(it.id, 'inmueble_nombre', e.target.value)}
                        disabled={!canEdit} placeholder="Nombre / direccion"
                        className={`${inputCls} h-8 text-xs`} />
                      {Array.isArray(it.data_discrepancies) && it.data_discrepancies.length > 0 && (
                        <button onClick={() => setDiscrepancyModal(it)} data-testid={`discrepancy-badge-${it.id}`}
                          className="mt-1.5 w-full text-left space-y-0.5 p-1.5 rounded bg-red-500/10 border border-red-500/30 hover:bg-red-500/15 transition-colors">
                          {it.data_discrepancies.slice(0, 4).map((d, i) => (
                            <p key={i} className="text-[9px] text-red-300 leading-tight flex items-start gap-1">
                              <AlertTriangle size={8} className="text-red-400 mt-0.5 shrink-0" />
                              <span className="min-w-0"><b className="text-red-100">{d.label}:</b> <span className="text-white/60">{d.wasi}</span> WASI <span className="text-red-300">≠</span> <span className="text-white/60">{d.web}</span> Web</span>
                            </p>
                          ))}
                          {it.data_discrepancies.length > 4 && (
                            <p className="text-[9px] text-red-400/80 italic pt-0.5">+ {it.data_discrepancies.length - 4} más — ver detalles</p>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <button onClick={() => canEdit && updateField(it.id, 'disponible', !it.disponible)} disabled={!canEdit}
                        className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${it.disponible ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-red-500/10 text-red-300 border-red-500/20'}`}>
                        {it.disponible ? 'SI' : 'NO'}
                      </button>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="text-xs text-white/70">{it.session_name}</span>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <a href={it.drive_link} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-[#A38560] hover:underline">
                        Abrir <ExternalLink size={11} />
                      </a>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[180px]">
                      <div className="flex flex-col gap-1.5">
                        {canEdit ? <StatusBadge id={it.id} field="link_wasi" value={it.link_wasi} /> : <span className="text-xs">{it.link_wasi}</span>}
                        <Input value={it.link_wasi_url || ''} onChange={e => updateFieldDebounced(it.id, 'link_wasi_url', e.target.value)}
                          disabled={!canEdit} placeholder="https://wasi..." className={`${inputCls} h-7 text-[11px]`} />
                        {it.link_wasi_url && <a href={it.link_wasi_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#A38560] hover:underline truncate">Abrir Wasi ↗</a>}
                        {it.link_wasi_auto_matched && it.link_wasi_url && (
                          <span data-testid={`wasi-auto-badge-${it.id}`} className="inline-flex items-center gap-1 text-[9px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-1.5 py-0.5 w-fit">
                            <Sparkles size={8} /> Auto-vinculado
                          </span>
                        )}
                        {it.wasi_match_suggestion && !it.link_wasi_url && (
                          <div data-testid={`wasi-suggestion-${it.id}`} className="p-1.5 rounded-md bg-[#A38560]/10 border border-[#A38560]/30 space-y-1">
                            <p className="text-[10px] text-[#A38560] leading-tight">
                              🎯 <b>{it.wasi_match_suggestion.matches}/{it.wasi_match_suggestion.total_drive_photos} fotos</b> coinciden con WASI {it.wasi_match_suggestion.id_property}
                            </p>
                            {isAdmin && (
                              <div className="flex gap-1">
                                <button onClick={() => acceptWasiSuggestion(it.id)} data-testid={`accept-wasi-${it.id}`}
                                  className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30">
                                  <Check size={9} /> Aceptar
                                </button>
                                <button onClick={() => dismissWasiSuggestion(it.id)} data-testid={`dismiss-wasi-${it.id}`}
                                  className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/10">
                                  <X size={9} /> Descartar
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {it.wasi_match_conflict && !it.link_wasi_url && isAdmin && (
                          <button onClick={() => setConflictModal(it)} data-testid={`wasi-conflict-${it.id}`}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 w-fit">
                            <AlertTriangle size={10} /> Conflicto ({(it.wasi_match_conflict.candidates || []).length} opciones)
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[170px]">
                      <div className="flex flex-col gap-1.5">
                        {canEdit ? <StatusBadge id={it.id} field="link_web" value={it.link_web} /> : <span className="text-xs">{it.link_web}</span>}
                        <Input value={it.link_web_url || ''} onChange={e => updateFieldDebounced(it.id, 'link_web_url', e.target.value)}
                          disabled={!canEdit} placeholder="https://web..." className={`${inputCls} h-7 text-[11px]`} />
                        {it.link_web_url && <a href={it.link_web_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#A38560] hover:underline truncate">Abrir Web ↗</a>}
                        {it.link_web_auto_matched && it.link_web_url && (
                          <span data-testid={`web-auto-badge-${it.id}`} className="inline-flex items-center gap-1 text-[9px] text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded-full px-1.5 py-0.5 w-fit">
                            <Sparkles size={8} /> Auto-vinculado
                          </span>
                        )}
                        {it.web_match_suggestion && !it.link_web_url && (
                          <div data-testid={`web-suggestion-${it.id}`} className="p-1.5 rounded-md bg-blue-500/10 border border-blue-500/30 space-y-1">
                            <p className="text-[10px] text-blue-300 leading-tight">
                              🎯 <b>{it.web_match_suggestion.matches}/{it.web_match_suggestion.total_drive_photos} fotos</b> coinciden en la web
                            </p>
                            {isAdmin && (
                              <div className="flex gap-1">
                                <button onClick={() => acceptWebsiteSuggestion(it.id)} data-testid={`accept-web-${it.id}`}
                                  className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30">
                                  <Check size={9} /> Aceptar
                                </button>
                                <button onClick={() => dismissWebsiteSuggestion(it.id)} data-testid={`dismiss-web-${it.id}`}
                                  className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/10">
                                  <X size={9} /> Descartar
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {it.in_wasi_not_in_web && it.link_wasi_url && !it.link_web_url && !it.web_match_suggestion && (
                          <span data-testid={`not-in-web-${it.id}`} className="text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/25 rounded-full px-1.5 py-0.5 w-fit">
                            ⚠️ En WASI, no en web
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      {canEdit ? <StatusBadge id={it.id} field="documentos" value={it.documentos} /> : <span className="text-xs">{it.documentos}</span>}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <Input value={it.notas || ''} onChange={e => updateFieldDebounced(it.id, 'notas', e.target.value)}
                        disabled={!canEdit} placeholder="Notas..." className={`${inputCls} h-8 text-xs`} />
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-3 align-top">
                        <div className="flex flex-col gap-1">
                          <button onClick={() => setMergeModal(it)} title="Fusionar con otro inmueble" className="p-1.5 rounded hover:bg-[#A38560]/10 text-white/30 hover:text-[#A38560]" data-testid={`merge-${it.id}`}>
                            <Layers size={14} />
                          </button>
                          <button onClick={() => deleteRow(it.id)} title="Eliminar fila" className="p-1.5 rounded hover:bg-red-500/10 text-white/30 hover:text-red-400" data-testid={`del-${it.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 11 : 10} className="text-center py-12 text-white/30 text-sm">
                  {items.length === 0 ? 'No hay inmuebles. Clic en "Sincronizar Drive" para importar.' : 'Sin resultados con los filtros actuales.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {filtered.map(it => {
          const canEdit = isAdmin || it.broker_user_id === user?.id;
          return (
            <div key={it.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 space-y-3">
              <div className="flex gap-3">
                <button onClick={() => canEdit && triggerUpload(it.id)} disabled={!canEdit}
                  className="w-20 h-20 rounded-lg overflow-hidden bg-[#03110D] border border-white/10 flex items-center justify-center shrink-0">
                  {(it.cover_image || it.cover_drive_id) ? <img src={it.cover_image || driveThumb(it.cover_drive_id)} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    : <div className="flex flex-col items-center text-white/30"><ImageIcon size={18} /><span className="text-[9px] uppercase mt-1">Portada</span></div>}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#A38560] font-medium">{it.broker_user_name}</p>
                  <p className="text-sm text-white mt-0.5 truncate">{it.session_name}</p>
                  <a href={it.drive_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#A38560] hover:underline mt-1">
                    Ver fotos <ExternalLink size={10} />
                  </a>
                </div>
                <button onClick={() => canEdit && updateField(it.id, 'disponible', !it.disponible)} disabled={!canEdit}
                  className={`shrink-0 self-start text-[10px] px-2.5 py-1 rounded-full border font-medium ${it.disponible ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-red-500/10 text-red-300 border-red-500/20'}`}>
                  {it.disponible ? 'DISP.' : 'NO DISP.'}
                </button>
              </div>
              <Input value={it.inmueble_nombre || ''} onChange={e => updateFieldDebounced(it.id, 'inmueble_nombre', e.target.value)}
                disabled={!canEdit} placeholder="Nombre / direccion del inmueble" className={`${inputCls} h-9`} />
              {Array.isArray(it.data_discrepancies) && it.data_discrepancies.length > 0 && (
                <button onClick={() => setDiscrepancyModal(it)} data-testid={`discrepancy-badge-mobile-${it.id}`}
                  className="w-full text-left space-y-1 p-2 rounded-lg bg-red-500/10 border border-red-500/30 hover:bg-red-500/15">
                  <p className="text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-1 mb-1"><AlertTriangle size={10} /> Inconsistencias WASI vs Web</p>
                  {it.data_discrepancies.slice(0, 4).map((d, i) => (
                    <p key={i} className="text-[10px] text-red-200 leading-tight">
                      <b className="text-red-100">{d.label}:</b> <span className="text-white/60">{d.wasi}</span> WASI <span className="text-red-300">≠</span> <span className="text-white/60">{d.web}</span> Web
                    </p>
                  ))}
                  {it.data_discrepancies.length > 4 && (
                    <p className="text-[10px] text-red-400/80 italic">+ {it.data_discrepancies.length - 4} más — ver detalles</p>
                  )}
                </button>
              )}
              {it.wasi_match_conflict && !it.link_wasi_url && isAdmin && (
                <button onClick={() => setConflictModal(it)} data-testid={`wasi-conflict-mobile-${it.id}`}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 w-full justify-center">
                  <AlertTriangle size={12} /> Conflicto WASI ({(it.wasi_match_conflict.candidates || []).length} opciones)
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2"><span className="text-[10px] text-white/30 uppercase tracking-wider w-10">Wasi</span>
                    {canEdit ? <StatusBadge id={it.id} field="link_wasi" value={it.link_wasi} /> : <span className="text-xs">{it.link_wasi}</span>}
                  </div>
                  <Input value={it.link_wasi_url || ''} onChange={e => updateFieldDebounced(it.id, 'link_wasi_url', e.target.value)}
                    disabled={!canEdit} placeholder="https://wasi..." className={`${inputCls} h-8 text-[11px]`} />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2"><span className="text-[10px] text-white/30 uppercase tracking-wider w-10">Web</span>
                    {canEdit ? <StatusBadge id={it.id} field="link_web" value={it.link_web} /> : <span className="text-xs">{it.link_web}</span>}
                  </div>
                  <Input value={it.link_web_url || ''} onChange={e => updateFieldDebounced(it.id, 'link_web_url', e.target.value)}
                    disabled={!canEdit} placeholder="https://web..." className={`${inputCls} h-8 text-[11px]`} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/30 uppercase tracking-wider">Docs</span>
                {canEdit ? <StatusBadge id={it.id} field="documentos" value={it.documentos} /> : <span className="text-xs">{it.documentos}</span>}
              </div>
              <Input value={it.notas || ''} onChange={e => updateFieldDebounced(it.id, 'notas', e.target.value)}
                disabled={!canEdit} placeholder="Notas..." className={`${inputCls} h-9`} />
              {isAdmin && (
                <div className="flex justify-end gap-3">
                  <button onClick={() => setMergeModal(it)} className="text-xs text-[#A38560]/70 hover:text-[#A38560] flex items-center gap-1" data-testid={`merge-mobile-${it.id}`}>
                    <Layers size={12} /> Fusionar
                  </button>
                  <button onClick={() => deleteRow(it.id)} className="text-xs text-red-400/70 hover:text-red-400 flex items-center gap-1">
                    <Trash2 size={12} /> Eliminar
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-white/30 text-sm bg-[#0A1F1A] rounded-2xl border border-white/5">
            {items.length === 0 ? 'No hay inmuebles. Sincroniza Drive para importar.' : 'Sin resultados.'}
          </div>
        )}
      </div>

      {/* Merge modal */}
      {mergeModal && (
        <MergeModal keeper={mergeModal} allItems={items} onClose={() => setMergeModal(null)} onConfirm={mergeRows} />
      )}

      {/* WASI conflict modal */}
      {conflictModal && (
        <div data-testid="wasi-conflict-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setConflictModal(null)}>
          <div className="bg-[#0A1F1A] rounded-2xl border border-red-500/30 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-white/5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} className="text-red-400" />
                  <h3 className="font-outfit text-base font-semibold text-white">Conflicto de coincidencia</h3>
                </div>
                <p className="text-xs text-white/60 truncate">{conflictModal.inmueble_nombre || conflictModal.session_name || '(sin nombre)'}</p>
                <p className="text-[10px] text-white/40 mt-1">Este inmueble tiene fotos EXACTAS coincidiendo con varias fichas WASI. Elige a cuál vincular, o elimina esta fila si es un duplicado.</p>
              </div>
              <button onClick={() => setConflictModal(null)} data-testid="close-conflict-modal"
                className="text-white/40 hover:text-white p-1 rounded"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-2">
              {(conflictModal.wasi_match_conflict?.candidates || []).map((c) => (
                <div key={c.id_property} className="bg-[#03110D] rounded-xl border border-white/5 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{c.title || `WASI ${c.id_property}`}</p>
                    <p className="text-[11px] text-white/50 mt-0.5">
                      <b className="text-emerald-400">{c.exact}</b> exactas / <b className="text-amber-300">{c.close}</b> cercanas de {c.total_drive_photos} fotos
                    </p>
                    <a href={c.wasi_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#A38560] hover:underline">{c.wasi_url}</a>
                  </div>
                  <button onClick={() => resolveWasiConflict(conflictModal.id, c.id_property)} data-testid={`resolve-conflict-${c.id_property}`}
                    className="shrink-0 text-[11px] px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 font-medium whitespace-nowrap">
                    <Check size={11} className="inline mr-1" /> Vincular
                  </button>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex flex-col sm:flex-row justify-between items-stretch gap-2">
              <button onClick={() => deleteInmuebleRow(conflictModal.id)} data-testid="delete-row-btn"
                className="text-[11px] px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 transition-colors flex items-center gap-2 justify-center">
                <Trash2 size={12} /> Eliminar esta fila
              </button>
              <button onClick={() => dismissWasiConflict(conflictModal.id)} data-testid="dismiss-conflict-btn"
                className="text-[11px] px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 transition-colors">
                Descartar aviso (dejar sin vincular)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discrepancy modal */}
      {discrepancyModal && (
        <div data-testid="discrepancy-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDiscrepancyModal(null)}>
          <div className="bg-[#0A1F1A] rounded-2xl border border-amber-500/30 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-white/5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <h3 className="font-outfit text-base font-semibold text-white">Datos inconsistentes</h3>
                </div>
                <p className="text-xs text-white/60 truncate">{discrepancyModal.inmueble_nombre || discrepancyModal.session_name}</p>
                <p className="text-[10px] text-white/40 mt-1">Comparando WASI vs somosselecto.com</p>
              </div>
              <button onClick={() => setDiscrepancyModal(null)} data-testid="close-discrepancy-modal"
                className="text-white/40 hover:text-white p-1 rounded"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3">
              {(discrepancyModal.data_discrepancies || []).map((d, i) => (
                <div key={i} className="bg-[#03110D] rounded-xl border border-white/5 p-4">
                  <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">{d.label}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-white/40 mb-1">En WASI</p>
                      <p className="text-sm text-white font-medium">{d.wasi || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#A38560] mb-1">En somosselecto.com</p>
                      <p className="text-sm text-[#A38560] font-medium">{d.web || '—'}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                {discrepancyModal.link_wasi_url && (
                  <a href={discrepancyModal.link_wasi_url} target="_blank" rel="noreferrer"
                    className="flex-1 text-center text-[11px] px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 transition-colors">
                    Abrir en WASI ↗
                  </a>
                )}
                {discrepancyModal.link_web_url && (
                  <a href={discrepancyModal.link_web_url} target="_blank" rel="noreferrer"
                    className="flex-1 text-center text-[11px] px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#A38560] border border-[#A38560]/25 transition-colors">
                    Abrir en la web ↗
                  </a>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex justify-between items-center gap-3">
              <p className="text-[10px] text-white/40">Revisa y actualiza el dato en WASI o en el sitio.</p>
              <button onClick={() => dismissDiscrepancies(discrepancyModal.id)} data-testid="dismiss-discrepancies-btn"
                className="text-[11px] px-3 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 transition-colors">
                Marcar como revisadas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change-log modal (WASI + Web) */}
      {historyOpen && (
        <div data-testid="history-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setHistoryOpen(false)}>
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/10 max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-white/5 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <History size={16} className="text-[#A38560]" />
                  <h3 className="font-outfit text-base font-semibold text-white">Historial de cambios</h3>
                </div>
                <p className="text-[11px] text-white/50 mt-1">Registro cronológico de todos los cambios detectados en WASI y en somosselecto.com</p>
              </div>
              <button onClick={() => setHistoryOpen(false)} className="text-white/40 hover:text-white p-1" data-testid="close-history-modal"><X size={18} /></button>
            </div>
            <div className="px-6 py-3 border-b border-white/5 flex items-center gap-2 flex-wrap">
              {[
                { k: 'all', label: 'Todos' },
                { k: 'wasi', label: 'WASI' },
                { k: 'web', label: 'Web' },
              ].map(t => (
                <button key={t.k} onClick={() => setHistoryFilter(t.k)} data-testid={`history-filter-${t.k}`}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${historyFilter === t.k
                    ? 'bg-[#A38560]/20 border-[#A38560]/40 text-[#A38560]'
                    : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
                  {t.label}
                </button>
              ))}
              <span className="ml-auto text-[10px] text-white/40">
                {historyLoading ? 'Cargando...' : `${historyEvents.filter(e => historyFilter === 'all' || e.source === historyFilter).length} eventos`}
              </span>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-white/5">
              {historyLoading && (
                <div className="py-16 text-center text-white/40 text-sm">Cargando historial...</div>
              )}
              {!historyLoading && historyEvents.length === 0 && (
                <div className="py-16 text-center text-white/40 text-sm">
                  Aún no hay cambios registrados.<br />
                  <span className="text-[11px] text-white/30">Los cambios aparecerán aquí automáticamente cada vez que WASI o la web detecten una modificación.</span>
                </div>
              )}
              {!historyLoading && historyEvents
                .filter(e => historyFilter === 'all' || e.source === historyFilter)
                .map(ev => {
                  const isWasi = ev.source === 'wasi';
                  const when = ev.detected_at ? new Date(ev.detected_at) : null;
                  const whenTxt = when ? when.toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                  return (
                    <div key={ev.id} data-testid={`history-event-${ev.id}`} className="px-6 py-3 hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`shrink-0 mt-1 w-8 h-8 rounded-lg flex items-center justify-center border ${isWasi ? 'bg-[#A38560]/10 border-[#A38560]/25' : 'bg-blue-500/10 border-blue-500/25'}`}>
                          {isWasi ? <Building2 size={14} className="text-[#A38560]" /> : <Globe size={14} className="text-blue-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium tracking-wide ${isWasi ? 'bg-[#A38560]/15 text-[#A38560] border-[#A38560]/25' : 'bg-blue-500/15 text-blue-300 border-blue-500/25'}`}>
                              {isWasi ? 'WASI' : 'WEB'}
                            </span>
                            <p className="text-[13px] text-white font-medium truncate">{ev.title || '(sin nombre)'}</p>
                          </div>
                          <p className="text-[12px] text-white/70 mt-1">{ev.message}</p>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[10px] text-white/40">
                            <span>{whenTxt}</span>
                            {ev.broker_name && <span>· Broker: <span className="text-white/60">{ev.broker_name}</span></span>}
                            {ev.link_wasi_url && (
                              <a href={ev.link_wasi_url} target="_blank" rel="noreferrer" data-testid={`history-link-wasi-${ev.id}`}
                                className="flex items-center gap-1 text-[#A38560] hover:text-[#B39670]">
                                <ExternalLink size={10} /> Ver en WASI
                              </a>
                            )}
                            {ev.link_web_url && (
                              <a href={ev.link_web_url} target="_blank" rel="noreferrer" data-testid={`history-link-web-${ev.id}`}
                                className="flex items-center gap-1 text-blue-300 hover:text-blue-200">
                                <ExternalLink size={10} /> Ver en la web
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MergeModal({ keeper, allItems, onClose, onConfirm }) {
  const [q, setQ] = useState('');
  const candidates = allItems.filter(x => x.id !== keeper.id);
  const term = q.trim().toLowerCase();
  const filtered = !term ? candidates.slice(0, 40) : candidates.filter(x =>
    (x.inmueble_nombre || '').toLowerCase().includes(term) ||
    (x.session_name || '').toLowerCase().includes(term) ||
    (x.broker_user_name || '').toLowerCase().includes(term) ||
    (x.link_wasi_url || '').toLowerCase().includes(term)
  ).slice(0, 40);

  const KeeperCard = ({ it, label, tone }) => (
    <div className={`bg-[#03110D] rounded-xl border ${tone === 'keep' ? 'border-emerald-500/25' : 'border-white/5'} p-3`}>
      <p className={`text-[9px] uppercase tracking-widest ${tone === 'keep' ? 'text-emerald-400' : 'text-white/40'} mb-1`}>{label}</p>
      <p className="text-sm text-white font-medium truncate">{it.inmueble_nombre || it.session_name || '(sin nombre)'}</p>
      <p className="text-[11px] text-white/50 truncate">{it.broker_user_name || it.broker_folder_name || '—'}</p>
      <div className="flex gap-1 mt-1.5 flex-wrap">
        {it.source === 'wasi' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/25">Solo WASI</span>}
        {it.link_wasi_url && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#A38560]/15 text-[#A38560] border border-[#A38560]/25">WASI ✓</span>}
        {it.link_web_url && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/25">Web ✓</span>}
        {it.subfolder_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">Drive ✓</span>}
      </div>
    </div>
  );

  return (
    <div data-testid="merge-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0A1F1A] rounded-2xl border border-[#A38560]/30 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-white/5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Layers size={16} className="text-[#A38560]" />
              <h3 className="font-outfit text-base font-semibold text-white">Fusionar inmuebles</h3>
            </div>
            <p className="text-[11px] text-white/50 mt-1">Se conserva el inmueble seleccionado y se rellenan los espacios vacíos con datos del otro. El otro será eliminado.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-merge-modal"><X size={18} /></button>
        </div>
        <div className="px-6 pt-4">
          <KeeperCard it={keeper} label="Se mantendrá" tone="keep" />
        </div>
        <div className="px-6 py-3 border-b border-white/5">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar inmueble a fusionar..." data-testid="merge-search"
              className="w-full h-10 bg-[#03110D] border border-white/10 rounded-lg pl-9 pr-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#A38560]/40" />
          </div>
        </div>
        <div className="overflow-y-auto p-4 space-y-2 flex-1">
          {filtered.length === 0 && <p className="text-center text-white/30 text-sm py-8">Sin resultados</p>}
          {filtered.map(it => (
            <div key={it.id} className="bg-[#03110D] rounded-xl border border-white/5 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white font-medium truncate">{it.inmueble_nombre || it.session_name || '(sin nombre)'}</p>
                <p className="text-[11px] text-white/50 truncate">{it.broker_user_name || it.broker_folder_name || '—'}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {it.source === 'wasi' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/25">Solo WASI</span>}
                  {it.link_wasi_url && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#A38560]/15 text-[#A38560] border border-[#A38560]/25">WASI ✓</span>}
                  {it.link_web_url && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/25">Web ✓</span>}
                  {it.subfolder_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">Drive ✓</span>}
                </div>
              </div>
              <button onClick={() => onConfirm(keeper.id, it.id)} data-testid={`merge-with-${it.id}`}
                className="shrink-0 text-[11px] px-3 py-2 rounded-lg bg-[#A38560]/20 hover:bg-[#A38560]/30 text-[#A38560] border border-[#A38560]/30 font-medium whitespace-nowrap">
                <Layers size={11} className="inline mr-1" /> Fusionar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
